// The orchestration that used to live inline in the React component: sign in,
// discover the experience, connect, keep the connection alive across drops and
// token expiry, and run one talk turn at a time. Framework free on purpose, so
// both glasses clients can drive it.
import { playerErrorText } from './playerErrorText';
import type { AuthCredentials, AuthPort, AuthSession } from '../../ports/AuthPort';
import type { ExperienceDiscoveryPort } from '../../ports/ExperienceDiscoveryPort';
import type {
  ActionProjection,
  ActionResponse,
  BootstrapBindings,
  ExperienceConfig,
  ExperienceConnection,
  ExperienceNavigation,
  ExperiencePort,
  MissionUpdate,
  TextTurnIntent,
  Utterance,
  UtteranceResponse,
  UtteranceSource,
} from '../../ports/ExperiencePort';
import type { ErrorFrame } from '../../ports/ErrorFrame';
import type { PresentationDirective } from './presentationDirectives';

export type SessionPhase =
  | 'idle'
  | 'connecting'
  | 'connected'
  | 'reconnecting'
  | 'offline'
  | 'stopped';

export interface SessionLine {
  speaker: string;
  text: string;
  /**
   * The utterance's source, when this line came from an experience.utterance
   * (Gaia's server-held-utterance-contract). Undefined for lines that arrive
   * some other way (an action_response's plain text, a mission_update's
   * speech). The presentation queue reads this to decide whether the line is
   * an opening/entry line eligible for staccato splitting, and whether it is
   * the step's live, unanswered prompt. See beats.ts.
   */
  source?: UtteranceSource;
  /** This line's presentation cues (presentationDirectives.ts). Undefined for lines that carry none. */
  presentationDirectives?: PresentationDirective[];
  /** The current step's authored display name, for the card header's status word. See DossierCard/o¿o ruling. */
  stepTitle?: string;
}

export interface SessionMissionState {
  missionId: string | null;
  phase: string | null;
  stepId: string | null;
}

/** One line of the exchange log: something said, sent, or noticed during a run. */
export interface ExchangeEntry {
  /** ISO time from the session's now() port. */
  at: string;
  who: 'player' | 'throckmorton' | 'system';
  kind: 'turn' | 'reply' | 'prompt' | 'status' | 'error';
  text: string;
  step?: string;
}

const EXCHANGE_LOG_CAP = 200;

export interface SessionState {
  phase: SessionPhase;
  /** Which step failed, in plain words. Null while the session is healthy. */
  stoppedReason: string | null;
  bindings: BootstrapBindings | null;
  line: SessionLine | null;
  turnInFlight: boolean;
  lastError: string | null;
  mission: SessionMissionState | null;
  /**
   * The character id the session should speak as, from the experience.config
   * frame or, failing that, an utterance's speaker.actorTemplateId. Null
   * until either has been seen.
   */
  characterId: string | null;
  /** Grows every time exchangeLog gains an entry, so subscribers re-render without holding the array in state. */
  exchangeCount: number;
  /**
   * The current step's answer contract, from the latest utterance whose
   * source carried one (Gaia PR #1030). Authoritative for the current step.
   * An utterance without the field leaves this unchanged: only entry lines
   * carry it, and conversation replies/acknowledgements/idle nudges must not
   * retract whatever answer affordance is already showing. Reset to null
   * whenever a new session starts.
   */
  authoritativeResponse: UtteranceResponse | null;
  /** Authored suggestions for the current server question; replies leave them in place. */
  answerChoices?: string[];
  /**
   * The server-authored compass target, or null when no navigation is active.
   * A new authoritative step or display.clear clears it. Presentation timing
   * belongs to the existing beat queue.
   */
  navigation: ExperienceNavigation | null;
}

export interface SessionCredentials {
  token: string;
  experienceId: string;
  /** The staffed run id (bindings.sessionId), the authority for correlation. */
  sessionId: string;
  /**
   * The transcription route's correlation field. Falls back to sessionId when
   * no room instance id was present on the bootstrap bindings.
   */
  roomInstanceId: string;
}

export interface ExperienceSessionPorts {
  auth: AuthPort;
  discovery: ExperienceDiscoveryPort;
  experience: ExperiencePort;
  now?: () => number;
  setTimeout?: typeof setTimeout;
  clearTimeout?: typeof clearTimeout;
}

export interface ExperienceSessionOptions {
  turnTimeoutMs?: number;
  renewBeforeMs?: number;
}

const DEFAULT_TURN_TIMEOUT_MS = 20_000;
const DEFAULT_RENEW_BEFORE_MS = 60_000;
const DEFAULT_SPEAKER = 'Throckmorton';

type TimerHandle = ReturnType<typeof setTimeout>;

export class ExperienceSession {
  private current: SessionState = {
    phase: 'idle',
    stoppedReason: null,
    bindings: null,
    line: null,
    turnInFlight: false,
    lastError: null,
    mission: null,
    characterId: null,
    exchangeCount: 0,
    authoritativeResponse: null,
    answerChoices: [],
    navigation: null,
  };

  private readonly log: ExchangeEntry[] = [];
  private readonly listeners = new Set<(state: SessionState) => void>();
  private readonly turnTimeoutMs: number;
  private readonly renewBeforeMs: number;

  private token: string | null = null;
  private experienceId: string | null = null;
  private refreshToken: string | null = null;
  private turnTimer: TimerHandle | null = null;
  private renewTimer: TimerHandle | null = null;
  private disposed = false;
  /** Step display names from experience.config, the header fallback when a line carries no step_title. */
  private stepTitles: Record<string, string> = {};
  private readonly seenUtteranceIds = new Set<string>();
  private pendingCanonicalUtteranceId: string | null = null;

  constructor(
    private readonly ports: ExperienceSessionPorts,
    private readonly authCredentials: AuthCredentials,
    options: ExperienceSessionOptions = {}
  ) {
    this.turnTimeoutMs = options.turnTimeoutMs ?? DEFAULT_TURN_TIMEOUT_MS;
    this.renewBeforeMs = options.renewBeforeMs ?? DEFAULT_RENEW_BEFORE_MS;
  }

  get state(): SessionState {
    return this.current;
  }

  /** Append-only, capped at 200 entries (oldest dropped first). */
  get exchangeLog(): readonly ExchangeEntry[] {
    return this.log;
  }

  /**
   * What a sibling that calls the gateway's other routes needs: the current
   * bearer token and the ids the transcription route takes as correlation
   * fields. Null until the session is connected, and null again after dispose.
   * Read at call time, never cached, so a renewed token is always the one sent.
   */
  credentials(): SessionCredentials | null {
    const bindings = this.current.bindings;
    if (this.disposed || this.current.phase === 'stopped' || !this.token || !bindings) return null;
    return {
      token: this.token,
      experienceId: bindings.experienceId,
      sessionId: bindings.sessionId,
      roomInstanceId: bindings.roomInstanceId ?? bindings.sessionId,
    };
  }

  subscribe(listener: (state: SessionState) => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  async start(): Promise<void> {
    if (this.disposed) return;
    this.pendingCanonicalUtteranceId = null;
    this.patch({ phase: 'connecting', stoppedReason: null, lastError: null, authoritativeResponse: null, answerChoices: [], navigation: null });

    let session: AuthSession;
    try {
      session = await this.ports.auth.login(this.authCredentials);
    } catch {
      this.stop('sign-in');
      return;
    }
    if (this.disposed) return;
    this.acceptSession(session);

    try {
      const discovery = await this.ports.discovery.discoverDefaultExperience(session.accessToken);
      this.experienceId = discovery.defaultExperienceId || discovery.experience.id;
    } catch {
      this.stop('experience discovery');
      return;
    }
    if (this.disposed) return;

    if (!this.openConnection()) return;
    this.scheduleRenewal(session.expiresAt);
  }

  sendTalk(text: string): boolean {
    const trimmed = text.trim();
    const bindings = this.current.bindings;
    if (!trimmed) return false;
    if (this.disposed || this.current.turnInFlight) return false;
    if (this.current.phase !== 'connected' || !bindings) return false;

    // The participant turn goes out on the staffed-session text_turn path (the
    // onboarding/conversation route): no client-supplied actor or room
    // binding, just bindings.sessionId, the text, and a request/idempotency
    // pair so the same turn can be retried with the same keys.
    const requestId = createRequestId('turn');
    const intent: TextTurnIntent = {
      sessionId: bindings.sessionId,
      text: trimmed,
      requestId,
      idempotencyKey: requestId,
    };

    try {
      this.ports.experience.sendTextTurn(intent);
    } catch {
      this.patch({ lastError: 'That message did not leave the device. Try sending again.' });
      return false;
    }

    this.appendExchange({ who: 'player', kind: 'turn', text: trimmed });
    this.patch({ turnInFlight: true, lastError: null });
    this.turnTimer = this.setTimer(() => {
      this.turnTimer = null;
      if (!this.current.turnInFlight) return;
      this.patch({ turnInFlight: false, lastError: turnTimeoutMessage(this.turnTimeoutMs) });
    }, this.turnTimeoutMs);
    return true;
  }

  /**
   * Sends the player's position with the server's existing update_location
   * message (the same one the phone web app uses): only while navigation is active
   * and the session is connected. Throttling (at most every 5s, only after a
   * 10m move) is the caller's job (see nav/throttle.ts) so this stays a thin
   * pass-through. Returns false when the frame was not sent.
   */
  sendPosition(lat: number, lng: number, accuracyM: number): boolean {
    if (this.disposed || this.current.phase !== 'connected') return false;
    if (!this.current.navigation?.target) return false;
    const bindings = this.current.bindings;
    if (!bindings) return false;
    try {
      this.ports.experience.sendLocation(lat, lng, accuracyM);
    } catch {
      return false;
    }
    return true;
  }

  async retry(): Promise<void> {
    if (this.disposed || this.current.phase !== 'stopped') return;
    this.patch({ stoppedReason: null });
    await this.start();
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.clearTurnTimer();
    this.clearRenewTimer();
    try {
      this.ports.experience.disconnect();
    } catch {
      // A port that is already closed is not an error worth surfacing here.
    }
    this.current = { ...this.current, phase: 'stopped', turnInFlight: false };
    this.listeners.clear();
  }

  // ---- connection ----

  /** Returns false when the connect call threw and the session has stopped. */
  private openConnection(): boolean {
    if (!this.token || !this.experienceId) {
      this.stop('connection');
      return false;
    }
    try {
      this.ports.experience.connect(this.token, this.experienceId, this.handlers());
    } catch {
      this.stop('connection');
      return false;
    }
    return true;
  }

  private handlers(): ExperienceConnection {
    return {
      onConnected: (bindings: BootstrapBindings) => {
        if (this.disposed) return;
        this.appendExchange({ who: 'system', kind: 'status', text: bootstrapStatusText('connected', bindings) });
        this.patch({ phase: 'connected', bindings, stoppedReason: null });
        try {
          this.ports.experience.sendRequestConfig(bindings.sessionId);
        } catch {
          // Config request is best-effort; a failure here does not stop the session.
        }
        try {
          this.ports.experience.sendEvents?.({
            sessionId: bindings.sessionId,
            events: [
              {
                eventId: createRequestId('evt'),
                eventType: 'session.started',
                occurredAt: new Date(this.now()).toISOString(),
                payload: {},
              },
            ],
          });
        } catch {
          // Telemetry is best-effort; a failure here does not stop the session.
        }
      },
      onUtterance: (utterance: Utterance) => {
        if (this.disposed) return;
        if (this.seenUtteranceIds.has(utterance.utteranceId)) return;
        this.seenUtteranceIds.add(utterance.utteranceId);
        const completesPendingTurn = this.pendingCanonicalUtteranceId === utterance.utteranceId;
        if (completesPendingTurn) {
          this.pendingCanonicalUtteranceId = null;
          this.clearTurnTimer();
        }

        const stepId = utterance.source?.stepId;
        const mission: SessionMissionState | null = stepId
          ? {
              missionId: this.current.mission?.missionId ?? null,
              phase: this.current.mission?.phase ?? null,
              stepId,
            }
          : this.current.mission;

        // A config frame's characterId is the authority; an utterance's
        // speaker.actorTemplateId only fills in when no config value has
        // arrived yet.
        const characterId = this.current.characterId ?? utterance.speaker.actorTemplateId ?? null;

        this.appendExchange({
          who: 'throckmorton',
          kind: 'prompt',
          text: utterance.text,
          step: stepId,
        });
        // source.response is authoritative when present; when absent (conversation
        // replies, acknowledgements, idle nudges) leave the current answer
        // affordance in place by omitting the field from this patch.
        const responseChange = utterance.source?.response
          ? { authoritativeResponse: utterance.source.response }
          : {};
        // A display.map presentation directive on a line is the only way
        // navigation state is set (build note #3): the server never sends a
        // standalone experience.navigation frame. Sim only ever sends
        // display.map from authored step data or its own state, never from
        // the model, so trusting it here carries no extra risk.
        const mapDirective = utterance.presentationDirectives?.find((d) => d.m === 'display.map');
        const navigationChange = mapDirective
          ? { navigation: navigationFromMapDirective(mapDirective, bindingsSessionId(this.current), stepId) }
          : utterance.presentationDirectives?.some((d) => d.m === 'display.clear')
            || (stepId && this.current.navigation?.stepId && stepId !== this.current.navigation.stepId)
            ? { navigation: null }
            : {};
        const choices = utterance.presentationDirectives?.find((d) => d.m === 'display.choice');
        const clearsChoices = utterance.presentationDirectives?.some((d) => d.m === 'display.clear')
          || (stepId && this.current.mission?.stepId && stepId !== this.current.mission.stepId)
          || utterance.source?.response?.awaiting === false;
        const choiceChange = choices ? { answerChoices: choices.p.options as string[] }
          : clearsChoices ? { answerChoices: [] } : {};
        this.patch({
          ...choiceChange,
          mission,
          characterId,
          line: {
            speaker: this.speakerForTemplate(utterance.speaker.actorTemplateId),
            text: utterance.text,
            source: utterance.source,
            presentationDirectives: utterance.presentationDirectives,
            stepTitle:
              utterance.stepTitle ??
              (utterance.source?.stepId ? this.stepTitles[utterance.source.stepId] : undefined),
          },
          ...responseChange,
          ...navigationChange,
          ...(completesPendingTurn ? { turnInFlight: false } : {}),
        });
      },
      onConfig: (config: ExperienceConfig) => {
        if (this.disposed) return;
        this.stepTitles = config.stepTitles ?? {};
        if (!config.characterId) return;
        this.patch({ characterId: config.characterId });
      },
      onActionResponse: (response: ActionResponse) => {
        if (this.disposed) return;
        const canonical = asRecord(asRecord(response.metadata)?.canonical_utterance);
        const hasCanonicalSpeech = response.success && canonical?.type === 'experience.utterance' && Boolean(pickString(canonical.utterance_id));
        const embeddedMissionUpdate = extractEmbeddedMissionUpdate(response);
        if (embeddedMissionUpdate) {
          this.applyMissionUpdate(embeddedMissionUpdate, !hasCanonicalSpeech);
        }
        const inlineSayText = inlineNpcSayText(response.projection?.inline_directives);
        // The response acknowledges the action; its admitted utterance owns speech.
        // This marker works regardless of which frame arrives first.
        const text = hasCanonicalSpeech ? undefined : response.projection?.plain_text || response.message || inlineSayText;
        if (text) {
          this.appendExchange({
            who: 'throckmorton',
            kind: response.success ? 'reply' : 'error',
            text: response.success ? text : `failed: ${text}`,
          });
        }
        const waitingForSpeech = hasCanonicalSpeech && !this.seenUtteranceIds.has(String(canonical?.utterance_id));
        this.pendingCanonicalUtteranceId = waitingForSpeech ? String(canonical?.utterance_id) : null;
        if (!waitingForSpeech) this.clearTurnTimer();
        this.patch({
          line: text ? { speaker: this.speaker(), text } : this.current.line,
          turnInFlight: waitingForSpeech ? this.current.turnInFlight : false,
          lastError: null,
        });
      },
      onMissionUpdate: (update: MissionUpdate) => {
        if (this.disposed) return;
        this.applyMissionUpdate(update);
      },
      onSimEvent: () => undefined,
      onWorldUpdate: () => undefined,
      onAreaOfInterest: () => undefined,
      onError: (frame: ErrorFrame) => {
        if (this.disposed) return;
        // Raw message/detail are diagnostics: console only, never the conversation.
        console.warn('[experience] error frame', { code: frame.code, message: frame.message, detail: frame.detail });
        const text = playerErrorText(frame);
        this.appendExchange({ who: 'system', kind: 'error', text });
        this.clearTurnTimer();
        this.patch({ turnInFlight: false, lastError: text });
      },
      onDisconnected: () => {
        if (this.disposed) return;
        this.clearTurnTimer();
        // The socket adapter retries on its own with a growing delay, so the
        // session only records the drop and waits for the next connected frame.
        // No talk is ever replayed.
        const phase = isOffline() ? 'offline' : 'reconnecting';
        this.appendExchange({ who: 'system', kind: 'status', text: phase });
        this.patch({ phase, turnInFlight: false });
      },
    };
  }

  // ---- token renewal ----

  private scheduleRenewal(expiresAt: number): void {
    this.clearRenewTimer();
    if (this.disposed) return;
    const delay = Math.max(0, expiresAt - this.renewBeforeMs - this.now());
    this.renewTimer = this.setTimer(() => {
      this.renewTimer = null;
      void this.renew();
    }, delay);
  }

  private async renew(): Promise<void> {
    if (this.disposed) return;
    try {
      let session: AuthSession;
      if (this.authCredentials.kind === 'device') {
        // Device sign-in issues no refresh token, so signing in again is the renewal.
        session = await this.ports.auth.login(this.authCredentials);
      } else {
        if (!this.refreshToken) throw new Error('no refresh token');
        session = await this.ports.auth.refresh(this.refreshToken);
      }
      if (this.disposed) return;
      this.acceptSession(session);
      this.scheduleRenewal(session.expiresAt);
      // The open socket stays as it is (the server checked the token when it
      // opened). Only the port's own later reconnects need the new token; a
      // reconnect here would greet the participant again.
      this.ports.experience.updateToken?.(session.accessToken);
    } catch {
      this.stop('sign-in renewal');
    }
  }

  private acceptSession(session: AuthSession): void {
    this.token = session.accessToken;
    this.refreshToken = session.refreshToken ?? null;
  }

  // ---- state plumbing ----

  private stop(reason: string): void {
    if (this.disposed) return;
    this.clearTurnTimer();
    this.clearRenewTimer();
    try {
      this.ports.experience.disconnect();
    } catch {
      // A port that is already closed is not an error worth surfacing here.
    }
    this.appendExchange({ who: 'system', kind: 'status', text: `stopped (${reason})` });
    this.patch({ phase: 'stopped', stoppedReason: reason, turnInFlight: false });
  }

  /**
   * Pushes to the log and bumps the count on `current` directly, without its
   * own notify: every call site follows this with a `patch()` of its own, and
   * that single patch carries the updated count out to listeners.
   */
  private appendExchange(entry: Omit<ExchangeEntry, 'at'>): void {
    this.log.push({ at: new Date(this.now()).toISOString(), ...entry });
    if (this.log.length > EXCHANGE_LOG_CAP) this.log.shift();
    this.current = { ...this.current, exchangeCount: this.current.exchangeCount + 1 };
  }

  private patch(changes: Partial<SessionState>): void {
    this.current = { ...this.current, ...changes };
    const snapshot = this.current;
    for (const listener of [...this.listeners]) {
      try {
        listener(snapshot);
      } catch {
        // One listener throwing must not stop the others or the caller.
      }
    }
  }

  private applyMissionUpdate(update: MissionUpdate, presentSpeech = true): void {
    const mission: SessionMissionState = {
      missionId: pickString(update.mission_id) ?? this.current.mission?.missionId ?? null,
      phase: pickString(update.phase) ?? this.current.mission?.phase ?? null,
      stepId: pickString(update.current_step) ?? this.current.mission?.stepId ?? null,
    };
    const speech = presentSpeech ? missionSpeechFromUpdate(update) : undefined;
    if (speech) {
      this.patch({ mission, line: { speaker: this.speakerFor(speech.characterId), text: speech.text } });
      return;
    }
    const fallbackText = presentSpeech ? missionSpeech(update) : undefined;
    if (fallbackText) {
      this.patch({ mission, line: { speaker: this.speaker(), text: fallbackText } });
      return;
    }
    this.patch({ mission });
  }

  private speakerFor(characterId: string | undefined): string {
    if (characterId) {
      const characters = this.current.bindings?.presentCharacters;
      const match = characters?.find((character) => character.characterId === characterId);
      if (match?.displayName) return match.displayName;
    }
    return this.speaker();
  }

  private speakerForTemplate(actorTemplateId: string | undefined): string {
    if (actorTemplateId) {
      const characters = this.current.bindings?.presentCharacters;
      const match = characters?.find((character) => character.characterTemplateId === actorTemplateId);
      if (match?.displayName) return match.displayName;
    }
    return this.speaker();
  }

  private speaker(): string {
    const characters = this.current.bindings?.presentCharacters;
    const named = characters?.find((character) => Boolean(character.displayName));
    return named?.displayName ?? DEFAULT_SPEAKER;
  }

  private now(): number {
    return this.ports.now ? this.ports.now() : Date.now();
  }

  private setTimer(run: () => void, delayMs: number): TimerHandle {
    const schedule = this.ports.setTimeout ?? ((fn: () => void, ms: number) => setTimeout(fn, ms));
    return schedule(run, delayMs) as TimerHandle;
  }

  private clearTimer(handle: TimerHandle): void {
    const cancel = this.ports.clearTimeout ?? ((h: TimerHandle) => clearTimeout(h));
    cancel(handle);
  }

  private clearTurnTimer(): void {
    if (this.turnTimer === null) return;
    this.clearTimer(this.turnTimer);
    this.turnTimer = null;
  }

  private clearRenewTimer(): void {
    if (this.renewTimer === null) return;
    this.clearTimer(this.renewTimer);
    this.renewTimer = null;
  }
}

function turnTimeoutMessage(timeoutMs: number): string {
  const seconds = Math.round(timeoutMs / 1000);
  return `No reply after ${seconds} seconds.`;
}

/**
 * The phase word, plus the adapter's bootstrap status when it supplies one.
 * BootstrapBindings carries no typed `status` field today, so this reads it
 * loosely and stays a no-op the moment none is present.
 */
function bootstrapStatusText(phase: string, bindings: BootstrapBindings): string {
  const status = (bindings as unknown as Record<string, unknown>).status;
  return typeof status === 'string' && status ? `${phase} (${status})` : phase;
}

/** One line per entry: `HH:MM:SS who kind [step] text`. The step segment is omitted when absent. */
export function formatExchangeLog(entries: readonly ExchangeEntry[]): string {
  return entries.map(formatExchangeEntry).join('\n');
}

function formatExchangeEntry(entry: ExchangeEntry): string {
  const time = new Date(entry.at).toISOString().slice(11, 19);
  const step = entry.step ? ` [${entry.step}]` : '';
  return `${time} ${entry.who} ${entry.kind}${step} ${entry.text}`;
}

function isOffline(): boolean {
  return typeof navigator !== 'undefined' && navigator.onLine === false;
}

function missionSpeech(update: MissionUpdate): string | null {
  const record = update as Record<string, unknown>;
  if (typeof record.plain_text === 'string' && record.plain_text) return record.plain_text;
  const projection = record.projection;
  if (projection && typeof projection === 'object') {
    const plain = (projection as Record<string, unknown>).plain_text;
    if (typeof plain === 'string' && plain) return plain;
  }
  return null;
}

/** Reads the mission speech line from mission_speech.directive.params.text. */
function missionSpeechFromUpdate(update: MissionUpdate): { text: string; characterId?: string } | null {
  const directive = update.mission_speech?.directive;
  const text = directive?.params?.text;
  if (typeof text !== 'string' || !text.trim()) return null;
  return { text, characterId: directive?.params?.character_id };
}

/**
 * Finds a `mission_update` embedded in an action_response: top level, under
 * `metadata`, or under `state_changes`. The server mirrors the same object
 * into all three locations on the first mission advancement.
 */
function extractEmbeddedMissionUpdate(response: ActionResponse): MissionUpdate | null {
  if (response.mission_update) return response.mission_update;
  const metadata = asRecord(response.metadata);
  const fromMetadata = metadata?.mission_update;
  if (isRecord(fromMetadata)) return fromMetadata as unknown as MissionUpdate;
  const stateChanges = asRecord(response.state_changes);
  const fromStateChanges = stateChanges?.mission_update;
  if (isRecord(fromStateChanges)) return fromStateChanges as unknown as MissionUpdate;
  return null;
}

/** The `npc.say` inline directive's text, if one is present with non-empty text. */
function inlineNpcSayText(directives: ActionProjection['inline_directives']): string | undefined {
  if (!Array.isArray(directives)) return undefined;
  for (const directive of directives) {
    if (directive?.m === 'npc.say') {
      const text = directive.p?.text;
      if (typeof text === 'string' && text.trim()) return text;
    }
  }
  return undefined;
}

/** The bindings' session id, or 'session' as a last resort (only reachable if a line arrives before onConnected, which the adapter never does). */
function bindingsSessionId(state: SessionState): string {
  return state.bindings?.sessionId ?? 'session';
}

/**
 * Builds the ExperienceNavigation shape session state carries, from a
 * validated display.map directive's params (presentationDirectives.ts
 * already checked target.lat/target.lng are numbers and view is compass or
 * map before this directive reached here).
 */
export function navigationFromMapDirective(
  directive: PresentationDirective,
  sessionId: string,
  stepId: string | undefined
): ExperienceNavigation {
  const target = directive.p.target as { lat: number; lng: number; label?: string; arrival_radius_m?: number };
  const show = directive.p.view === 'map' ? 'map' : 'compass';
  return {
    type: 'experience.navigation',
    sessionId,
    stepId,
    target: {
      lat: target.lat,
      lng: target.lng,
      label: target.label,
      arrivalRadiusM: typeof target.arrival_radius_m === 'number' ? target.arrival_radius_m : 40,
    },
    show,
  };
}

function pickString(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function asRecord(value: unknown): Record<string, unknown> | undefined {
  return isRecord(value) ? value : undefined;
}

function createRequestId(prefix: string): string {
  const randomUuid = globalThis.crypto?.randomUUID;
  if (typeof randomUuid === 'function') {
    return randomUuid.call(globalThis.crypto);
  }
  const randomPart = Math.random().toString(36).slice(2, 10);
  return `${prefix}-${Date.now().toString(36)}-${randomPart}`;
}
